import React, { useEffect, useState } from "react";
import axios from "axios";
import dataEn from "../dataEn";
import dataKin from "../dataKin";
import { language } from "../tools/language";
import { Modal, Checkbox, Button, Popconfirm, message, Form, Input, Select, DatePicker, Drawer } from "antd";
import * as XLSX from 'xlsx';
import { FaFileExcel, FaEdit, FaTrash, FaFilter, FaSearch, FaChevronLeft, FaChevronRight, FaEye } from "react-icons/fa";
import "../assets/css/custom-checkbox.css";
import dayjs from 'dayjs';
import API_BASE_URL from "../config";

const { Option } = Select;

const data = language === "en" ? dataEn : dataKin || {};
const childrenTable = data && data.childrenTable ? data.childrenTable : {};

function AllChildren() {
  // State management
  const [children, setChildren] = useState([]);
  const [filteredChildren, setFilteredChildren] = useState([]);
  const [parents, setParents] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [showFilters, setShowFilters] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isExportModalVisible, setIsExportModalVisible] = useState(false);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [isViewDrawerVisible, setIsViewDrawerVisible] = useState(false);
  const [viewingChild, setViewingChild] = useState(null);
  const [editingChild, setEditingChild] = useState(null);
  const [form] = Form.useForm();
  const [selectedColumns, setSelectedColumns] = useState([]);
  const [isMobile, setIsMobile] = useState(false);

  // Check if device is mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Table columns configuration
  const allColumns = [
    'full_name', 'parent_national_id', 'parent_name', 'date_of_birth', 'age', 'status'
  ];
  
  const columnLabels = {
    full_name: "Full Name",
    parent_national_id: "Parent National ID",
    parent_name: "Parent Name",
    date_of_birth: "Date of Birth",
    age: "Age",
    status: "Status"
  };

  // Fetch data on component mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [childrenResponse, parentsResponse] = await Promise.all([
          axios.get(`${API_BASE_URL}/child`),
          axios.get(`${API_BASE_URL}/christian`)
        ]);

        setChildren(childrenResponse.data);
        setFilteredChildren(childrenResponse.data);
        
        const parentsMap = {};
        parentsResponse.data.forEach(parent => {
          parentsMap[parent.national_id] = parent.full_name;
        });
        setParents(parentsMap);
        
        setLoading(false);
      } catch (error) {
        console.error("Error fetching data:", error);
        message.error("Failed to load data");
        setLoading(false);
      }
    };
    
    fetchData();
    setSelectedColumns([...allColumns]);
  }, []);

  // Calculate age from DD/MM/YYYY format
  const calculateAge = (dateString) => {
    if (!dateString) return 0;
    
    try {
      const [day, month, year] = dateString.split('/').map(Number);
      const today = new Date();
      const currentYear = today.getFullYear();
      const currentMonth = today.getMonth() + 1;
      const currentDay = today.getDate();
      
      let age = currentYear - year;
      
      if (currentMonth < month || (currentMonth === month && currentDay < day)) {
        age--;
      }
      
      return age;
    } catch (error) {
      console.error("Error calculating age:", error);
      return 0;
    }
  };

  const toggleColumn = (column) => {
    setSelectedColumns(prev => 
      prev.includes(column) 
        ? prev.filter(c => c !== column) 
        : [...prev, column]
    );
  };

  const exportToExcel = () => {
    const filteredData = filteredChildren.map(child => {
      const obj = {};
      selectedColumns.forEach(col => {
        if (col === 'parent_name') {
          obj[col] = parents[child.parent_national_id] || 'Unknown';
        } else if (col === 'age') {
          obj[col] = calculateAge(child.date_of_birth);
        } else {
          obj[col] = child[col];
        }
      });
      return obj;
    });

    const worksheet = XLSX.utils.json_to_sheet(filteredData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Children");
    XLSX.writeFile(workbook, "children_data.xlsx");
    message.success("Export successful!");
  };

  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
  };

  const applyFilters = () => {
    let filtered = children;

    if (searchTerm) {
      filtered = filtered.filter(child => 
        child.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (parents[child.parent_national_id] || '').toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (statusFilter) {
      filtered = filtered.filter(child => child.status === statusFilter);
    }

    setFilteredChildren(filtered);
    setCurrentPage(1);
    if (isMobile) setShowFilters(false);
  };

  const clearFilters = () => {
    setSearchTerm("");
    setStatusFilter("");
    setFilteredChildren(children);
    setCurrentPage(1);
    if (isMobile) setShowFilters(false);
    message.info("Filters cleared");
  };

  const handleEdit = (child) => {
    const [day, month, year] = child.date_of_birth.split('/');
    const formattedDate = `${year}-${month}-${day}`;
    
    setEditingChild({
      ...child,
      date_of_birth: child.date_of_birth ? dayjs(formattedDate) : null
    });
    form.setFieldsValue({
      ...child,
      date_of_birth: child.date_of_birth ? dayjs(formattedDate) : null
    });
    setIsEditModalVisible(true);
  };

  const handleView = (child) => {
    setViewingChild(child);
    setIsViewDrawerVisible(true);
  };

  const handleEditSubmit = async () => {
    try {
      const values = await form.validateFields();
      const formattedDate = values.date_of_birth 
        ? values.date_of_birth.format('DD/MM/YYYY') 
        : null;
      
      const updatedChild = {
        ...values,
        date_of_birth: formattedDate
      };

      const response = await axios.put(`${API_BASE_URL}/child/${editingChild.id}`, updatedChild);
      
      if (response.status === 200) {
        message.success("Child updated successfully");
        setIsEditModalVisible(false);
        
        setChildren(children.map(c => c.id === editingChild.id ? updatedChild : c));
        setFilteredChildren(filteredChildren.map(c => c.id === editingChild.id ? updatedChild : c));
      } else {
        throw new Error("Failed to update child");
      }
    } catch (error) {
      console.error("Error updating child:", error);
      message.error(error.response?.data?.message || "Failed to update child");
    }
  };

  const handleDelete = async (childId) => {
    try {
      const response = await axios.delete(`${API_BASE_URL}/child/${childId}`);
      
      if (response.status === 200) {
        message.success("Child deleted successfully");
        setChildren(children.filter(c => c.id !== childId));
        setFilteredChildren(filteredChildren.filter(c => c.id !== childId));
      } else {
        throw new Error("Failed to delete child");
      }
    } catch (error) {
      console.error("Error deleting child:", error);
      message.error(error.response?.data?.message || "Failed to delete child");
    }
  };

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const paginateData = () => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = currentPage * itemsPerPage;
    return filteredChildren.slice(startIndex, endIndex);
  };

  const totalPages = Math.ceil(filteredChildren.length / itemsPerPage);

  const ExportModal = () => (
    <Modal
      title="Select Columns to Export"
      open={isExportModalVisible}
      onOk={() => {
        exportToExcel();
        setIsExportModalVisible(false);
      }}
      onCancel={() => setIsExportModalVisible(false)}
      okText="Export"
      cancelText="Cancel"
      width={isMobile ? '90%' : 800}
      okButtonProps={{
        style: { 
          background: '#164e63', 
          borderColor: '#164e63',
        },
      }}
    >
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: isMobile ? 'repeat(1, 1fr)' : 'repeat(3, 1fr)', 
        gap: '10px' 
      }}>
        {allColumns.map((column) => (
          <Checkbox
            key={column}
            checked={selectedColumns.includes(column)}
            onChange={() => toggleColumn(column)}
            className="custom-checkbox"
          >
            {columnLabels[column] || column.replace(/_/g, ' ')}
          </Checkbox>
        ))}
      </div>
    </Modal>
  );

  const EditModal = () => (
    <Modal
      title="Edit Child"
      open={isEditModalVisible}
      onOk={handleEditSubmit}
      onCancel={() => setIsEditModalVisible(false)}
      okText="Save"
      cancelText="Cancel"
      width={isMobile ? '90%' : 600}
      okButtonProps={{
        style: { 
          background: '#164e63', 
          borderColor: '#164e63',
        },
      }}
    >
      <Form form={form} layout="vertical">
        <Form.Item
          name="full_name"
          label="Full Name"
          rules={[{ required: true, message: 'Please input the full name!' }]}
        >
          <Input />
        </Form.Item>
        
        <Form.Item
          name="parent_national_id"
          label="Parent National ID"
          rules={[{ required: true, message: 'Please input the parent national ID!' }]}
        >
          <Input />
        </Form.Item>
        
        <Form.Item
          name="date_of_birth"
          label="Date of Birth"
        >
          <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} />
        </Form.Item>
        
        <Form.Item
          name="status"
          label="Status"
          rules={[{ required: true, message: 'Please select the status!' }]}
        >
          <Select>
            <Option value="Yarakiriwe">Yarakiriwe</Option>
            <Option value="Ntarakirwa">Ntarakirwa</Option>
          </Select>
        </Form.Item>
      </Form>
    </Modal>
  );

  const ViewDrawer = () => (
    <Drawer
      title="Child Details"
      placement="right"
      onClose={() => setIsViewDrawerVisible(false)}
      open={isViewDrawerVisible}
      width={isMobile ? '100%' : 400}
    >
      {viewingChild && (
        <div className="space-y-4">
          <div className="border-b pb-2">
            <h3 className="font-semibold text-gray-600">Full Name</h3>
            <p className="text-lg">{viewingChild.full_name}</p>
          </div>
          <div className="border-b pb-2">
            <h3 className="font-semibold text-gray-600">Parent National ID</h3>
            <p>{viewingChild.parent_national_id}</p>
          </div>
          <div className="border-b pb-2">
            <h3 className="font-semibold text-gray-600">Parent Name</h3>
            <p>{parents[viewingChild.parent_national_id] || 'Unknown'}</p>
          </div>
          <div className="border-b pb-2">
            <h3 className="font-semibold text-gray-600">Date of Birth</h3>
            <p>{viewingChild.date_of_birth}</p>
          </div>
          <div className="border-b pb-2">
            <h3 className="font-semibold text-gray-600">Age</h3>
            <p>{calculateAge(viewingChild.date_of_birth)} years</p>
          </div>
          <div className="border-b pb-2">
            <h3 className="font-semibold text-gray-600">Status</h3>
            <span className={`px-2 py-1 rounded-full text-xs ${
              viewingChild.status === 'Yarakiriwe' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
            }`}>
              {viewingChild.status}
            </span>
          </div>
        </div>
      )}
    </Drawer>
  );

  if (loading) {
    return (
      <div className="content flex justify-center items-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#164e63] mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Mobile Card View
  const MobileCardView = () => (
    <div className="space-y-3">
      {paginateData().map((child, index) => {
        const age = calculateAge(child.date_of_birth);
        const isOlderThan12 = age >= 12;
        
        return (
          <div
            key={child.id}
            className={`bg-white rounded-lg shadow-md p-4 ${isOlderThan12 ? 'border-l-4 border-yellow-400' : ''}`}
          >
            <div className="flex justify-between items-start mb-3">
              <div>
                <h3 className="font-bold text-lg text-gray-800">{child.full_name}</h3>
                <p className="text-sm text-gray-500">ID: {child.parent_national_id}</p>
              </div>
              <div className="flex gap-2">
                <Button
                  type="text"
                  icon={<FaEye />}
                  onClick={() => handleView(child)}
                  className="text-blue-600"
                />
                <Button
                  type="text"
                  icon={<FaEdit />}
                  onClick={() => handleEdit(child)}
                  className="text-green-600"
                />
                <Popconfirm
                  title="Delete this child?"
                  onConfirm={() => handleDelete(child.id)}
                  okText="Yes"
                  cancelText="No"
                >
                  <Button
                    type="text"
                    icon={<FaTrash />}
                    className="text-red-600"
                  />
                </Popconfirm>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <span className="text-gray-500">Parent:</span>
                <p className="font-medium">{parents[child.parent_national_id] || 'Unknown'}</p>
              </div>
              <div>
                <span className="text-gray-500">DOB:</span>
                <p className="font-medium">{child.date_of_birth}</p>
              </div>
              <div>
                <span className="text-gray-500">Age:</span>
                <p className="font-medium">{age} years</p>
              </div>
              <div>
                <span className="text-gray-500">Status:</span>
                <span className={`inline-block px-2 py-1 rounded-full text-xs ${
                  child.status === 'Yarakiriwe' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                }`}>
                  {child.status}
                </span>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );

  return (
    <div className="content px-3 sm:px-5">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mt-3 sm:mt-0">
        <h2 className="text-lg sm:text-xl font-bold text-[#164e63]">
          {childrenTable.title || "Children List"}
        </h2>
        <div className="text-sm text-gray-500">
          Total: <span className="font-bold text-[#164e63] text-lg">{filteredChildren.length}</span> children
        </div>
      </div>

      {/* Search and Filter Bar */}
      <div className="mt-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1 relative">
            <Input
              type="text"
              value={searchTerm}
              onChange={handleSearch}
              placeholder="Search by name or parent..."
              className="pl-10"
              prefix={<FaSearch className="text-gray-400" />}
              allowClear
            />
          </div>
          
          <div className="flex gap-2 flex-wrap">
            <Button 
              onClick={() => setShowFilters(!showFilters)}
              icon={<FaFilter />}
              className="bg-[#164e63] text-white"
            >
              {showFilters ? "Hide" : "Filter"}
            </Button>
            
            <Button 
              onClick={applyFilters}
              className="bg-[#164e63] text-white"
            >
              Apply
            </Button>
            
            <Button 
              onClick={clearFilters}
              className="bg-[#5a7021] text-white"
            >
              Clear
            </Button>
            
            <Button 
              onClick={() => setIsExportModalVisible(true)}
              icon={<FaFileExcel />}
              className="bg-[#217346] text-white"
            >
              Export
            </Button>
          </div>
        </div>
        
        {/* Filter Options */}
        {showFilters && (
          <div className="mt-3 p-4 bg-gray-50 rounded-lg">
            <Select
              value={statusFilter}
              onChange={setStatusFilter}
              className="w-full sm:w-64"
              placeholder="Filter by status"
              allowClear
            >
              <Option value="Yarakiriwe">Yarakiriwe</Option>
              <Option value="Ntarakirwa">Ntarakirwa</Option>
            </Select>
          </div>
        )}
      </div>

      {/* Main Content */}
      <div className="intro-y box p-3 sm:p-5 mt-5">
        {/* Items per page selector */}
        <div className="flex justify-between items-center mb-4">
          <div className="flex items-center gap-2">
            <span className="text-sm">Show:</span>
            <Select 
              value={itemsPerPage} 
              onChange={setItemsPerPage}
              className="w-20"
              size="small"
            >
              <Option value={10}>10</Option>
              <Option value={25}>25</Option>
              <Option value={50}>50</Option>
              <Option value={100}>100</Option>
            </Select>
          </div>
        </div>
        
        {/* Desktop Table View */}
        {!isMobile && (
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="bg-[#ADC2CB]">
                  <th className="px-4 py-3 rounded-l-lg text-left text-xs font-bold text-[#164E63] uppercase">No.</th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-[#164E63] uppercase">Full Name</th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-[#164E63] uppercase">Parent Name</th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-[#164E63] uppercase">DOB</th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-[#164E63] uppercase">Age</th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-[#164E63] uppercase">Status</th>
                  <th className="px-4 py-3 rounded-r-lg text-left text-xs font-bold text-[#164E63] uppercase">Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginateData().map((child, index) => {
                  const age = calculateAge(child.date_of_birth);
                  const isOlderThan12 = age >= 12;
                  
                  return (
                    <tr key={child.id} className={`border-b ${isOlderThan12 ? 'bg-yellow-50' : ''}`}>
                      <td className="px-4 py-3 text-sm">{(currentPage - 1) * itemsPerPage + index + 1}</td>
                      <td className="px-4 py-3 text-sm font-medium">{child.full_name}</td>
                      <td className="px-4 py-3 text-sm">{parents[child.parent_national_id] || 'Unknown'}</td>
                      <td className="px-4 py-3 text-sm">{child.date_of_birth}</td>
                      <td className="px-4 py-3 text-sm">{age}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded-full text-xs ${
                          child.status === 'Yarakiriwe' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}>
                          {child.status}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex gap-2">
                          <Button size="small" icon={<FaEye />} onClick={() => handleView(child)} />
                          <Button size="small" icon={<FaEdit />} onClick={() => handleEdit(child)} />
                          <Popconfirm
                            title="Delete this child?"
                            onConfirm={() => handleDelete(child.id)}
                            okText="Yes"
                            cancelText="No"
                          >
                            <Button size="small" danger icon={<FaTrash />} />
                          </Popconfirm>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        
        {/* Mobile Card View */}
        {isMobile && <MobileCardView />}
        
        {/* Pagination */}
        {filteredChildren.length > 0 && (
          <div className="flex flex-col sm:flex-row justify-between items-center gap-3 mt-4">
            <div className="text-sm text-gray-600 order-2 sm:order-1">
              Showing {(currentPage - 1) * itemsPerPage + 1} to {Math.min(currentPage * itemsPerPage, filteredChildren.length)} of {filteredChildren.length}
            </div>
            <div className="flex items-center gap-2 order-1 sm:order-2">
              <Button
                disabled={currentPage === 1}
                onClick={() => handlePageChange(currentPage - 1)}
                icon={<FaChevronLeft />}
                size="small"
              >
                Prev
              </Button>
              <span className="text-sm">
                Page {currentPage} of {totalPages || 1}
              </span>
              <Button
                disabled={currentPage === totalPages || totalPages === 0}
                onClick={() => handlePageChange(currentPage + 1)}
                icon={<FaChevronRight />}
                size="small"
              >
                Next
              </Button>
            </div>
          </div>
        )}
        
        {filteredChildren.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            No children found
          </div>
        )}
      </div>

      <ExportModal />
      <EditModal />
      <ViewDrawer />
    </div>
  );
}

export default AllChildren;